import React, { useState, useEffect } from 'react'
import { useRouteMatch } from 'react-router-dom'
import axios from 'axios'
import { articleSearchApi } from '../../api/articleSearchApi'
import { routes } from '../../constantes/routes'

/**
 * Las otras categorías de un artículo.
 *
 * Cuáles son "las otras" depende de dónde se pinte, y son dos criterios
 * distintos, no uno con excepciones:
 *
 *   · En la ficha del Catálogo se llega al producto NAVEGANDO por una
 *     categoría, así que sobra ésa y no la principal. Entrando a Carruseles
 *     por Material Sanitario, lo útil es "También en: Gestión de Medicación";
 *     repetir Material Sanitario no dice nada y esconde la que interesa.
 *
 *   · En el formulario de BackOffice no hay categoría de entrada. Ahí el
 *     punto de referencia es la principal, la que fija SelectCategory, y se
 *     listan las adicionales.
 *
 * La categoría de entrada se saca de la ruta y no de una prop. El componente
 * ya se busca sus propios datos por la misma razón: puede colocarse en
 * pantallas del motor antiguo con una sola línea, sin hacer llegar nada a
 * través de sus contenedores. Fuera del Catálogo v2 no hay match, y entonces
 * el criterio pasa a ser la principal, que es justo lo que quiere el
 * formulario.
 *
 * Se pinta sólo si queda alguna: un producto con una sola categoría —la
 * enorme mayoría— no muestra nada, ni un hueco ni una etiqueta vacía.
 *
 * El coste es una petición por pantalla, no por fila; para listados, que sí
 * tienen muchas filas, se usa el `categorias[]` que ya viene en la respuesta
 * de búsqueda.
 *
 * Si falla, no pinta nada: es información complementaria, y un producto sin
 * categorías alternativas se ve exactamente igual que un error silencioso.
 */
const CategoriasAlternativas = ({ idArticulo, etiqueta = 'También en', style }) => {
  const [categorias, setCategorias] = useState([])

  // Sin `exact`: vale igual para la ficha colgando de la categoría que para
  // una anidada varios niveles de familia por debajo.
  const rutaCategoria = useRouteMatch({
    path: `${routes.CATALOGUE_V2}/categoria/:idCategoria`,
  })
  const idCategoriaActual = rutaCategoria ? rutaCategoria.params.idCategoria : null

  useEffect(() => {
    if (!idArticulo) {
      setCategorias([])
      return undefined
    }

    const fuente = axios.CancelToken.source()

    articleSearchApi
      .article(idArticulo, fuente.token)
      .then((res) => setCategorias((res.data && res.data.categorias) || []))
      .catch(() => setCategorias([]))

    return () => fuente.cancel()
  }, [idArticulo])

  const sobra =
    idCategoriaActual !== null
      ? (c) => String(c.id) === String(idCategoriaActual)
      : (c) => c.esPrincipal

  const alternativas = categorias.filter((c) => !sobra(c))

  if (!alternativas.length) return null

  return (
    <p
      style={{
        fontSize: 12,
        opacity: 0.7,
        margin: '4px 0 0',
        lineHeight: 1.4,
        ...style,
      }}
    >
      {etiqueta}: {alternativas.map((c) => c.nombre).join(', ')}
    </p>
  )
}

export default CategoriasAlternativas
